import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import CompetitorTagsSelector from '@/components/ui/competitor-tags-selector';
import { DealerSurveyData } from '@/api/types';
import { useTranslation } from 'react-i18next';

interface FormStepThreeProps {
  formData: DealerSurveyData;
  updateFormData: (formData: Partial<DealerSurveyData>) => void;
  readOnly?: boolean;
}

const FormStepThree: React.FC<FormStepThreeProps> = ({ formData, updateFormData, readOnly = false }) => {
  const { t } = useTranslation('common');

  const handleRadioChange = (field: keyof DealerSurveyData, value: string) => {
    updateFormData({ [field]: value });
  };

  const handleTextChange = (field: keyof DealerSurveyData, value: string) => {
    updateFormData({ [field]: value });
  };

  const handleCompetitorsChange = (tags: string[]) => {
    updateFormData({ competitors: tags });
  };

  return (
    <div className={`space-y-8 ${readOnly ? 'pointer-events-none opacity-75' : ''}`}>
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <p className="text-orange-800 text-sm">
          <strong>{t('form.required_fields')}:</strong> {t('form_step_2.warning')}
        </p>
      </div>

      {/* Co zaujalo / Co chybělo */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="whatAttracted" className="text-slate-700 font-medium">
            {t('form_step_2.what_attracted')}
          </Label>
          <Input
            id="whatAttracted"
            value={formData.whatAttracted}
            onChange={(e) => handleTextChange('whatAttracted', e.target.value)}
            placeholder={t('form_step_2.what_attracted_placeholder')}
            className="border-slate-300 focus:border-amber-500"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="whatMissing" className="text-slate-700 font-medium">
            {t('form_step_2.what_missing')}
          </Label>
          <Input
            id="whatMissing"
            value={formData.whatMissing}
            onChange={(e) => handleTextChange('whatMissing', e.target.value)}
            placeholder={t('form_step_2.what_missing_placeholder')}
            className="border-slate-300 focus:border-amber-500"
          />
        </div>
      </div>

      <CompetitorTagsSelector
        label={t('form_step_2.competitors')}
        value={formData.competitors || []}
        onChange={handleCompetitorsChange}
        placeholder={t('form_step_2.competitors_placeholder')}
        className="border-slate-300 focus:border-amber-500"
      />

      {/* Koncová cena */}
      <div className="space-y-4">
        <Label className="text-lg font-semibold text-slate-800">
          {t('form_step_2.price_perception_title')}
        </Label>
        <RadioGroup
          value={formData.pricePerception}
          onValueChange={(value) => handleRadioChange('pricePerception', value)}
          className="space-y-3"
        >
          {[
            { value: 'fair-value', label: t('form_step_2.price_fair_value') },
            { value: 'higher-expected', label: t('form_step_2.price_higher_expected') },
            { value: 'surprisingly-good', label: t('form_step_2.price_surprisingly_good') }
          ].map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <RadioGroupItem value={option.value} id={`price-${option.value}`} />
              <Label htmlFor={`price-${option.value}`} className="text-slate-700">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Regionální potenciál */}
      <div className="space-y-4">
        <Label className="text-lg font-semibold text-slate-800">
          {t('form_step_2.region_potential_title')}
        </Label>
        <RadioGroup
          value={formData.regionPotential}
          onValueChange={(value) => handleRadioChange('regionPotential', value)}
          className="grid grid-cols-1 md:grid-cols-3 gap-3"
        >
          {[
            { value: 'yes', label: t('form_step_2.region_yes') },
            { value: 'maybe', label: t('form_step_2.region_maybe') },
            { value: 'no', label: t('form_step_2.region_no') }
          ].map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <RadioGroupItem value={option.value} id={`region-${option.value}`} />
              <Label htmlFor={`region-${option.value}`} className="text-slate-700">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Zájem o vzorek */}
      <div className="space-y-4">
        <Label className="text-lg font-semibold text-slate-800">
          {t('form_step_2.showroom_interest_title')}
        </Label>
        <RadioGroup
          value={formData.showroomInterest}
          onValueChange={(value) => handleRadioChange('showroomInterest', value)}
          className="space-y-3"
        >
          {[
            { value: 'buy-display', label: t('form_step_2.showroom_buy_display') },
            { value: 'buy-connect', label: t('form_step_2.showroom_buy_connect') },
            { value: 'shop-in-shop', label: t('form_step_2.showroom_shop_in_shop') },
            { value: 'maybe', label: t('form_step_2.showroom_maybe') },
            { value: 'no', label: t('form_step_2.showroom_no') }
          ].map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <RadioGroupItem value={option.value} id={`showroom-${option.value}`} />
              <Label htmlFor={`showroom-${option.value}`} className="text-slate-700">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Preferovaná barva do showroomu */}
      <div className="space-y-4">
        <Label className="text-lg font-semibold text-slate-800">
          {t('form_step_2.showroom_color_preference_title')}
        </Label>
        <RadioGroup
          value={formData.showroomColorPreference}
          onValueChange={(value) => handleRadioChange('showroomColorPreference', value)}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {[
            { value: 'antracit', label: t('form_step_2.color_antracit'), image: 'https://storage.archevio.com/c/data/148/legenda/1699.png' },
            { value: 'silver-grey', label: t('form_step_2.color_silver_grey'), image: 'https://storage.archevio.com/c/data/148/legenda/1700.png' },
            { value: 'pearl-beige', label: t('form_step_2.color_pearl_beige'), image: 'https://storage.archevio.com/c/data/148/legenda/1701.png' },
            { value: 'mauve', label: t('form_step_2.color_mauve'), image: 'https://storage.archevio.com/c/data/148/legenda/1702.png' },
            { value: 'pearl-brown', label: t('form_step_2.color_pearl_brown'), image: 'https://storage.archevio.com/c/data/148/legenda/1703.png' },
            { value: 'red-brown', label: t('form_step_2.color_red_brown'), image: 'https://storage.archevio.com/c/data/148/legenda/1704.png' }
          ].map((option) => (
            <div 
              key={option.value} 
              className="relative flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer"
              onClick={() => handleRadioChange('showroomColorPreference', option.value)}
            >
              <RadioGroupItem value={option.value} id={`color-${option.value}`} className="shrink-0" />
              <Label htmlFor={`color-${option.value}`} className="flex items-center gap-3 cursor-pointer flex-1">
                <img 
                  src={option.image} 
                  alt={option.label}
                  className="w-10 h-10 rounded-md object-cover border border-slate-200"
                  loading="lazy"
                />
                <span className="text-slate-700 font-medium">{option.label}</span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Marketingové materiály */}
      <div className="space-y-4">
        <Label className="text-lg font-semibold text-slate-800">
          {t('form_step_2.marketing_materials_title')}
        </Label>
        <RadioGroup
          value={formData.marketingMaterials}
          onValueChange={(value) => handleRadioChange('marketingMaterials', value)}
          className="grid grid-cols-1 md:grid-cols-2 gap-3"
        >
          {[
            { value: 'excellent', label: t('form_step_2.marketing_excellent') },
            { value: 'interesting', label: t('form_step_2.marketing_interesting') },
            { value: 'standard', label: t('form_step_2.marketing_standard') },
            { value: 'not-interested', label: t('form_step_2.marketing_not_interested') }
          ].map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <RadioGroupItem value={option.value} id={`marketing-${option.value}`} />
              <Label htmlFor={`marketing-${option.value}`} className="text-slate-700">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>
    </div>
  );
};

export default FormStepThree; 